import { FirestoreService } from '../../../../sdk/firestoreService';

/**
 * Screen Actions - פעולות CRUD למסכים
 * 
 * פעולות זמינות:
 * - CREATE: יצירת מסך חדש
 * - READ: קריאת רשימת מסכים
 * - UPDATE: עדכון פרטי מסך
 * - DELETE: מחיקת מסך
 */

// ============================================
// Action Creators
// ============================================

// READ - Fetch Screens List
export const fetchScreensStart = () => ({
    type: 'FETCH_SCREENS_START'
});

export const fetchScreensSuccess = (screens) => ({
    type: 'FETCH_SCREENS_SUCCESS',
    payload: screens
});

export const fetchScreensFailure = (error) => ({
    type: 'FETCH_SCREENS_FAILURE',
    payload: error
});

// CREATE
export const createScreenStart = () => ({
    type: 'CREATE_SCREEN_START'
});

export const createScreenSuccess = (screen, playlist, dashboard) => ({
    type: 'CREATE_SCREEN_SUCCESS',
    payload: { screen, playlist, dashboard }
});

export const createScreenFailure = (error) => ({
    type: 'CREATE_SCREEN_FAILURE',
    payload: error
});

// UPDATE
export const updateScreenStart = () => ({
    type: 'UPDATE_SCREEN_START'
});

export const updateScreenSuccess = (screen) => ({
    type: 'UPDATE_SCREEN_SUCCESS',
    payload: screen
});

export const updateScreenFailure = (error) => ({
    type: 'UPDATE_SCREEN_FAILURE',
    payload: error
});

// DELETE
export const deleteScreenStart = () => ({
    type: 'DELETE_SCREEN_START'
});

export const deleteScreenSuccess = (screenId, playlistIds) => ({
    type: 'DELETE_SCREEN_SUCCESS',
    payload: { screenId, playlistIds }
});

export const deleteScreenFailure = (error) => ({
    type: 'DELETE_SCREEN_FAILURE',
    payload: error
});

// ============================================
// Thunk Actions - CRUD Operations
// ============================================

/**
 * READ - קריאת כל המסכים של ארגון
 * @param {string} organizationId - מזהה הארגון
 */
export const fetchScreens = (organizationId) => async (dispatch) => {
    dispatch(fetchScreensStart());
    try {
        const screens = await FirestoreService.getScreensByOrganization(organizationId);
        dispatch(fetchScreensSuccess(screens));
    } catch (error) {
        console.error('Error fetching screens:', error);
        dispatch(fetchScreensFailure('שגיאה בטעינת המסכים'));
    }
};

/**
 * CREATE - יצירת מסך חדש עם פלייליסט ודשבורד ברירת מחדל
 * @param {string} organizationId - מזהה הארגון
 * @param {Object} screenData - נתוני המסך החדש
 * @param {string} screenData.name - שם המסך
 * @param {string} screenData.description - תיאור המסך (אופציונלי)
 */
export const createScreen = (organizationId, screenData) => async (dispatch) => {
    dispatch(createScreenStart());
    try {

        // 1. יצירת דשבורד ברירת מחדל (עם תבנית אם נבחרה)
        const dashboard = await FirestoreService.createDashboard(organizationId, {
            name: `לוח - ${screenData.name}`,
            description: 'לוח ברירת מחדל',
            createInitialVersion: false,  // לא יוצג עד publish
            templateWidgets: screenData.templateWidgets || null,
            templateSettings: screenData.templateSettings || null
        });

        // 2. יצירת המסך תחילה (ללא schedule)
        const screen = await FirestoreService.createScreen(organizationId, {
            ...screenData,
            schedule: {} // schedule ריק בינתיים
        });

        // 3. יצירת פלייליסט ברירת מחדל עם הדשבורד
        const playlist = await FirestoreService.createPlaylist(organizationId, {
            name: `מערך תצוגה - ${screenData.name}`,
            description: 'מערך תצוגה ברירת מחדל',
            items: [
                {
                    dashboardId: dashboard.dashboardId,
                    duration: 30, // 30 שניות ברירת מחדל
                    transition: 'fade' // אנימציה ברירת מחדל
                }
            ]
        }, screen.screenId); // עכשיו screen כבר קיים!

        // 4. עדכון המסך עם schedule ברירת מחדל
        const defaultSchedule = {};
        for (let day = 0; day < 7; day++) {
            defaultSchedule[day] = [
                {
                    playlistId: playlist.playlistId,
                    startTime: '00:00',
                    endTime: '23:59'
                }
            ];
        }

        await FirestoreService.updateScreen(screen.screenId, {
            schedule: defaultSchedule
        });

        // עדכון האובייקט המקומי
        screen.schedule = defaultSchedule;

        dispatch(createScreenSuccess(screen, playlist, dashboard));
        return { ...screen, dashboard };
    } catch (error) {
        console.error('Error creating screen:', error);
        dispatch(createScreenFailure('שגיאה ביצירת המסך'));
        throw error;
    }
};

/**
 * UPDATE - עדכון מסך קיים (אופטימיסטי)
 * @param {string} screenId - מזהה המסך
 * @param {Object} updates - השדות לעדכון
 * @param {string} updates.name - שם המסך (אופציונלי)
 * @param {string} updates.description - תיאור המסך (אופציונלי)
 * @param {Array} updates.schedule - לוח זמנים (אופציונלי)
 */
export const updateScreen = (screenId, updates) => async (dispatch, getState) => {
    dispatch(updateScreenStart());
    try {
        console.log('🟡 Action: updateScreen started');

        // שמירה ב-Firestore קודם
        console.log('🟡 Action: Saving to Firestore...');
        await FirestoreService.updateScreen(screenId, updates);
        console.log('🟡 Action: Firestore save completed!');

        // עדכון Redux רק אחרי שה-Firestore הצליח
        const currentScreen = getState().screens.screens.find(s => s.screenId === screenId);
        const updatedScreen = {
            ...currentScreen,
            ...updates,
            updatedAt: new Date()
        };

        dispatch(updateScreenSuccess(updatedScreen));
        console.log('🟡 Action: Redux updated after Firestore');

        return updatedScreen;
    } catch (error) {
        console.error('🔴 Action: Error updating screen:', error);
        dispatch(updateScreenFailure('שגיאה בעדכון המסך'));
        throw error;
    }
};

/**
 * DELETE - מחיקת מסך
 * @param {string} screenId - מזהה המסך למחיקה
 * @param {Array} playlistIds - מערך מזהי הפלייליסטים של המסך למחיקה
 */
export const deleteScreen = (screenId, playlistIds = []) => async (dispatch) => {
    dispatch(deleteScreenStart());
    try {
        // מחיקת כל הפלייליסטים של המסך
        await Promise.all(
            playlistIds.map(playlistId => FirestoreService.deletePlaylist(playlistId))
        );

        // מחיקת המסך
        await FirestoreService.deleteScreen(screenId);

        dispatch(deleteScreenSuccess(screenId, playlistIds));
    } catch (error) {
        console.error('Error deleting screen:', error);
        dispatch(deleteScreenFailure('שגיאה במחיקת המסך'));
        throw error;
    }
};

// ============================================
// Playlist Actions (merged from Playlist container)
// ============================================

// Fetch Playlists Actions
export const fetchPlaylistsStart = () => ({
    type: 'FETCH_PLAYLISTS_START'
});

export const fetchPlaylistsSuccess = (playlists) => ({
    type: 'FETCH_PLAYLISTS_SUCCESS',
    payload: playlists
});

export const fetchPlaylistsFailure = (error) => ({
    type: 'FETCH_PLAYLISTS_FAILURE',
    payload: error
});

// Create Playlist Actions
export const createPlaylistStart = () => ({
    type: 'CREATE_PLAYLIST_START'
});

export const createPlaylistSuccess = (playlist, screenId, updatedSchedule) => ({
    type: 'CREATE_PLAYLIST_SUCCESS',
    payload: { playlist, screenId, updatedSchedule }
});

export const createPlaylistFailure = (error) => ({
    type: 'CREATE_PLAYLIST_FAILURE',
    payload: error
});

// Update Playlist Actions
export const updatePlaylistStart = () => ({
    type: 'UPDATE_PLAYLIST_START'
});

export const updatePlaylistSuccess = (playlist) => ({
    type: 'UPDATE_PLAYLIST_SUCCESS',
    payload: playlist
});

export const updatePlaylistFailure = (error) => ({
    type: 'UPDATE_PLAYLIST_FAILURE',
    payload: error
});

// Delete Playlist Actions
export const deletePlaylistStart = () => ({
    type: 'DELETE_PLAYLIST_START'
});

export const deletePlaylistSuccess = (playlistId, screenId, updatedSchedule) => ({
    type: 'DELETE_PLAYLIST_SUCCESS',
    payload: { playlistId, screenId, updatedSchedule }
});

export const deletePlaylistFailure = (error) => ({
    type: 'DELETE_PLAYLIST_FAILURE',
    payload: error
});

// Thunk Actions for Playlists
export const fetchPlaylists = (organizationId) => async (dispatch) => {
    dispatch(fetchPlaylistsStart());
    try {
        const playlists = await FirestoreService.getPlaylistsByOrganization(organizationId);
        dispatch(fetchPlaylistsSuccess(playlists));
    } catch (error) {
        console.error('Error fetching playlists:', error);
        dispatch(fetchPlaylistsFailure('שגיאה בטעינת הפלייליסטים'));
    }
};

// שאילתה חדשה - פלייליסטים לפי מסך
export const fetchPlaylistsByScreen = (screenId) => async (dispatch) => {
    dispatch(fetchPlaylistsStart());
    try {
        const playlists = await FirestoreService.getPlaylistsByScreen(screenId);
        dispatch(fetchPlaylistsSuccess(playlists));
    } catch (error) {
        console.error('Error fetching playlists by screen:', error);
        dispatch(fetchPlaylistsFailure('שגיאה בטעינת הפלייליסטים'));
    }
};

export const createPlaylist = (organizationId, playlistData, screenId, currentSchedule) => async (dispatch) => {
    dispatch(createPlaylistStart());
    try {
        console.log('📋 יוצר פלייליסט חדש:', playlistData.name, 'למסך:', screenId);
        const playlist = await FirestoreService.createPlaylist(organizationId, playlistData, screenId);

        console.log('✅ פלייליסט נוצר בהצלחה עם screenId:', screenId);

        // מחזירים את ה-schedule הנוכחי ללא שינוי
        dispatch(createPlaylistSuccess(playlist, screenId, currentSchedule));
        return playlist;
    } catch (error) {
        console.error('Error creating playlist:', error);
        dispatch(createPlaylistFailure('שגיאה ביצירת הפלייליסט'));
        throw error;
    }
};

export const updatePlaylist = (playlistId, updates) => async (dispatch) => {
    dispatch(updatePlaylistStart());
    try {
        await FirestoreService.updatePlaylist(playlistId, updates);
        const updatedPlaylist = await FirestoreService.getPlaylist(playlistId);
        dispatch(updatePlaylistSuccess(updatedPlaylist));
    } catch (error) {
        console.error('Error updating playlist:', error);
        dispatch(updatePlaylistFailure('שגיאה בעדכון הפלייליסט'));
        throw error;
    }
};

export const deletePlaylist = (playlistId, screenId, currentSchedule) => async (dispatch) => {
    dispatch(deletePlaylistStart());
    try {
        // הסרת הפלייליסט מכל הימים ב-schedule
        const updatedSchedule = { ...currentSchedule };

        for (let day = 0; day < 7; day++) {
            if (updatedSchedule[day]) {
                updatedSchedule[day] = updatedSchedule[day].filter(item => item.playlistId !== playlistId);
            }
        }

        // עדכון המסך ב-Firestore
        await FirestoreService.updateScreen(screenId, { schedule: updatedSchedule });

        // מחיקת הפלייליסט
        await FirestoreService.deletePlaylist(playlistId);

        dispatch(deletePlaylistSuccess(playlistId, screenId, updatedSchedule));
    } catch (error) {
        console.error('Error deleting playlist:', error);
        dispatch(deletePlaylistFailure('שגיאה במחיקת הפלייליסט'));
        throw error;
    }
};

// ============================================
// Dashboard Actions (for Playlist management)
// ============================================

// Fetch Dashboards Actions
export const fetchDashboardsStart = () => ({
    type: 'FETCH_DASHBOARDS_START'
});

export const fetchDashboardsSuccess = (dashboards) => ({
    type: 'FETCH_DASHBOARDS_SUCCESS',
    payload: dashboards
});

export const fetchDashboardsFailure = (error) => ({
    type: 'FETCH_DASHBOARDS_FAILURE',
    payload: error
});

// Fetch Single Dashboard Actions
export const fetchDashboardStart = () => ({
    type: 'FETCH_DASHBOARD_START'
});

export const fetchDashboardSuccess = (dashboard) => ({
    type: 'FETCH_DASHBOARD_SUCCESS',
    payload: dashboard
});

export const fetchDashboardFailure = (error) => ({
    type: 'FETCH_DASHBOARD_FAILURE',
    payload: error
});

// Create Dashboard Actions
export const createDashboardStart = () => ({
    type: 'CREATE_DASHBOARD_START'
});

export const createDashboardSuccess = (dashboard, playlistId, updatedItems) => ({
    type: 'CREATE_DASHBOARD_SUCCESS',
    payload: { dashboard, playlistId, updatedItems }
});

export const createDashboardFailure = (error) => ({
    type: 'CREATE_DASHBOARD_FAILURE',
    payload: error
});

// Update Dashboard Actions
export const updateDashboardStart = () => ({
    type: 'UPDATE_DASHBOARD_START'
});

export const updateDashboardSuccess = (dashboard) => ({
    type: 'UPDATE_DASHBOARD_SUCCESS',
    payload: dashboard
});

export const updateDashboardFailure = (error) => ({
    type: 'UPDATE_DASHBOARD_FAILURE',
    payload: error
});

// Delete Dashboard Actions
export const deleteDashboardStart = () => ({
    type: 'DELETE_DASHBOARD_START'
});

export const deleteDashboardSuccess = (dashboardId, playlistId, updatedItems) => ({
    type: 'DELETE_DASHBOARD_SUCCESS',
    payload: { dashboardId, playlistId, updatedItems }
});

export const deleteDashboardFailure = (error) => ({
    type: 'DELETE_DASHBOARD_FAILURE',
    payload: error
});

// Thunk Actions for Dashboards
export const fetchDashboards = (organizationId) => async (dispatch) => {
    console.log('🔄 fetchDashboards - Starting...', { organizationId });
    dispatch(fetchDashboardsStart());
    try {
        const dashboards = await FirestoreService.getDashboardsByOrganization(organizationId);
        dispatch(fetchDashboardsSuccess(dashboards));
    } catch (error) {
        console.error('❌ fetchDashboards - Error:', error);
        dispatch(fetchDashboardsFailure('שגיאה בטעינת הדשבורדים'));
    }
};

export const fetchDashboard = (dashboardId) => async (dispatch) => {
    dispatch(fetchDashboardStart());
    try {
        const dashboard = await FirestoreService.getDashboard(dashboardId);
        dispatch(fetchDashboardSuccess(dashboard));
        return dashboard;
    } catch (error) {
        console.error('Error fetching dashboard:', error);
        dispatch(fetchDashboardFailure('שגיאה בטעינת הדשבורד'));
        throw error;
    }
};

export const createDashboardInPlaylist = (organizationId, dashboardData, playlistId, currentItems) => async (dispatch) => {
    dispatch(createDashboardStart());
    try {
        console.log('📊 יוצר דשבורד חדש בפלייליסט:', playlistId);

        // יצירת דשבורד חדש
        const dashboard = await FirestoreService.createDashboard(organizationId, {
            name: dashboardData.name,
            description: dashboardData.description,
            templateWidgets: dashboardData.templateWidgets,
            templateSettings: dashboardData.templateSettings,
            createInitialVersion: false  // לא יוצג עד publish
        });

        // הוספת הדשבורד לפלייליסט
        const newItem = {
            dashboardId: dashboard.dashboardId,
            duration: 30, // 30 שניות ברירת מחדל
            transition: 'fade' // אנימציה ברירת מחדל
        };
        const updatedItems = [...(currentItems || []), newItem];

        // עדכון הפלייליסט ב-Firestore
        await FirestoreService.updatePlaylist(playlistId, { items: updatedItems });

        console.log('✅ דשבורד נוסף לפלייליסט בהצלחה');
        dispatch(createDashboardSuccess(dashboard, playlistId, updatedItems));
        return dashboard;
    } catch (error) {
        console.error('Error creating dashboard in playlist:', error);
        dispatch(createDashboardFailure('שגיאה ביצירת הדשבורד'));
        throw error;
    }
};

export const updateDashboardInPlaylist = (dashboardId, updates) => async (dispatch) => {
    dispatch(updateDashboardStart());
    try {
        await FirestoreService.updateDashboardNew(dashboardId, updates);
        const updatedDashboard = await FirestoreService.getDashboard(dashboardId);
        dispatch(updateDashboardSuccess(updatedDashboard));
    } catch (error) {
        console.error('Error updating dashboard:', error);
        dispatch(updateDashboardFailure('שגיאה בעדכון הדשבורד'));
        throw error;
    }
};

export const deleteDashboardFromPlaylist = (dashboardId, playlistId, currentItems) => async (dispatch) => {
    dispatch(deleteDashboardStart());
    try {
        console.log('🗑️ מוחק דשבורד מפלייליסט:', dashboardId);

        // הסרת הדשבורד מהפלייליסט
        const updatedItems = (currentItems || []).filter(item => item.dashboardId !== dashboardId);

        // עדכון הפלייליסט ב-Firestore
        await FirestoreService.updatePlaylist(playlistId, { items: updatedItems });

        // מחיקת הדשבורד
        await FirestoreService.deleteDashboardNew(dashboardId);

        console.log('✅ דשבורד נמחק מהפלייליסט בהצלחה');
        dispatch(deleteDashboardSuccess(dashboardId, playlistId, updatedItems));
    } catch (error) {
        console.error('Error deleting dashboard from playlist:', error);
        dispatch(deleteDashboardFailure('שגיאה במחיקת הדשבורד'));
        throw error;
    }
};
